import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api";
import { motion, AnimatePresence } from "framer-motion";
import { FaLock, FaEnvelope } from "react-icons/fa";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const customImages = [
    // Add your image links here, for example:
    "https://i.postimg.cc/kgJHkKwW/1.jpg",
    // "https://example.com/your-image-2.jpg",
  ];

  const [nekoIndex, setNekoIndex] = useState(0);
  const nekoUrl =
    customImages[nekoIndex] || "https://i.postimg.cc/kgJHkKwW/1.jpg";

  // Initialize Google Identity Services button
  React.useEffect(() => {
    const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!googleClientId) return;
    const renderButton = () => {
      try {
        /* global google */
        if (!window.google || !window.google.accounts) return;
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: async (response) => {
            // response.credential is the ID token
            try {
              setIsLoading(true);
              await loginWithGoogle(response.credential);
              navigate(location.state?.redirectAfterLogin || "/");
            } catch (err) {
              console.error("Google login failed:", err);
              setError("Google sign-in failed. Please try again.");
            } finally {
              setIsLoading(false);
            }
          },
        });
        window.google.accounts.id.renderButton(
          document.getElementById("google-signin-button"),
          { theme: "outline", size: "large" }
        );
        // Mark that initialize completed so prompt() is safe to call
        window._gisInitialized = true;
        window._gisClientId = googleClientId;
      } catch (e) {
        console.error("Failed to render Google Sign-In button", e);
      }
    };

    renderButton();
  }, [loginWithGoogle, navigate, location.state]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const data = await login(email, password);

      // Handle successful login: if an anime was queued to add, call backend
      const animeToAdd = location.state?.animeToAdd;
      if (animeToAdd && data.uid) {
        try {
          await api.post(`/users/${data.uid}/wishlist`, {
            animeId: animeToAdd,
          });
        } catch (e) {
          console.error("Failed to add wishlist item after login:", e);
        }
      }

      navigate(location.state?.redirectAfterLogin || "/");
    } catch (error) {
      console.error("Login error:", error);
      // Backend returns structured errors
      if (error.response && error.response.data && error.response.data.error) {
        setError(error.response.data.error);
      } else {
        setError("Login failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Google sign-in disabled; use email/password or OTP flows.

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        when: "beforeChildren",
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
      },
    },
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black/40 backdrop-blur-sm">
      {/* Floating anime particles */}

      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-4xl px-4 sm:px-6 py-8 z-60 pointer-events-auto"
      >
        <div className="flex flex-col lg:flex-row items-stretch bg-transparent">
          {/* Left-side neko image (fetched) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="hidden lg:flex lg:w-1/2 rounded-l-3xl overflow-hidden relative"
            style={{ background: "#0b0b0b", height: "560px" }}
          >
            <img
              src={nekoUrl}
              alt="neko"
              className="w-full h-full object-cover"
              style={{ height: "560px" }}
              onError={(e) => {
                e.currentTarget.src = "https://i.postimg.cc/kgJHkKwW/1.jpg";
              }}
            />

            {/* Simple preview controls for testing multiple links */}
            {customImages.length > 1 && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                <button
                  onClick={() =>
                    setNekoIndex(
                      (i) => (i - 1 + customImages.length) % customImages.length
                    )
                  }
                  className="px-3 py-1 rounded bg-black/40 text-white"
                >
                  ◀
                </button>
                <button
                  onClick={() =>
                    setNekoIndex((i) => (i + 1) % customImages.length)
                  }
                  className="px-3 py-1 rounded bg-black/40 text-white"
                >
                  ▶
                </button>
              </div>
            )}
          </motion.div>

          {/* Glassmorphism form container */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="backdrop-blur-xl bg-white/5 rounded-3xl lg:rounded-r-3xl lg:rounded-l-none shadow-2xl overflow-hidden border border-white/10 lg:w-1/2"
            style={{
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)",
            }}
          >
            <div className="px-8 py-12">
              <div
                variants={itemVariants}
                className="text-4xl font-bold text-center text-white mb-8"
              >
                Welcome Back
              </div>

              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="mb-6 p-3 bg-red-500/20 text-red-100 rounded-lg text-sm border border-red-500/30"
                  >
                    {error}
                  </motion.div>
                )}
              </AnimatePresence>

              <form onSubmit={handleLogin} className="space-y-6">
                <motion.div variants={itemVariants} className="space-y-6">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaEnvelope className="text-white/50" />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email"
                      required
                      className="w-full pl-10 pr-4 py-3 bg-darkGrayl rounded-xl border border-white/10  text-white placeholder-white/40 outline-none transition duration-300 hover:border-white/20"
                    />
                  </div>

                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaLock className="text-white/50" />
                    </div>
                    <input
                      value={password}
                      type="password"
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password"
                      required
                      className="w-full pl-10 pr-10 py-3 rounded-xl border bg-darkGrayl border-white/10 text-white placeholder-white/40 outline-none transition duration-300 hover:border-white/20"
                    />
                  </div>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <motion.button
                    type="submit"
                    disabled={isLoading}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full py-4 px-6 rounded-xl text-white font-bold transition-all duration-300 ${
                      isLoading
                        ? "bg-darkGray cursor-not-allowed"
                        : "bg-darkGrayl"
                    }`}
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg
                          className="animate-spin h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                        </svg>
                        Authenticating...
                      </span>
                    ) : (
                      <span className="block transform transition duration-300 hover:scale-105">
                        Login
                      </span>
                    )}
                  </motion.button>

                  {/* Google sign-in button */}
                  <div className="mt-4 flex flex-col items-center gap-3">
                    <div id="g_id_onload" style={{ display: "none" }} />
                    <div
                      className="w-full"
                      id="google-signin-button"
                      aria-hidden="true"
                    />
                  </div>
                </motion.div>
              </form>

              <motion.div
                variants={itemVariants}
                className="mt-8 text-center text-sm"
              >
                <Link
                  to="/forgot-password"
                  className="text-red-400 hover:underline font-semibold"
                >
                  Forgot Password?
                </Link>
                <span className="mx-2 text-white/30">•</span>
                <Link
                  to="/signup"
                  className="text-indigo-400 hover:text-indigo-300 transition duration-300"
                >
                  Create new account
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Decorative floating elements */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.8, duration: 1 }}
          className="absolute -bottom-20 -left-20 w-40 h-40 bg-indigo-600 rounded-full mix-blend-overlay filter blur-3xl opacity-20"
        />
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 1, duration: 1 }}
          className="absolute top-1/4 -right-20 w-32 h-32 bg-pink-600 rounded-full mix-blend-overlay filter blur-3xl opacity-20"
        />
      </motion.div>
    </div>
  );
};

export default Login;
